import { inject, Injectable } from '@angular/core';
import { App } from '@capacitor/app';
import { LocalNotifications, LocalNotificationSchema } from '@capacitor/local-notifications';
import { Platform } from '@ionic/angular/standalone';
import { TranslateService } from '@ngx-translate/core';
import { addMinutes } from 'date-fns';
import { TranslationKeys } from 'src/app/contracts';
import { UI } from 'src/app/core/helpers/ui';

@Injectable({ providedIn: 'root' })
export class PendingPaymentReminderService {
  private ui = inject(UI);
  private translate = inject(TranslateService);
  private platform = inject(Platform);

  private readonly NOTIF_ID_BASE = 1000; // base to avoid collisions
  private currentRequestId: string | null = null;

  constructor() {
    this.listenForNotifications();
  }

  /** Schedule the native reminder for 7 minutes from now */
  public async scheduleLocalReminder(requestId: number, bookingIdentifier: string) {
    const { display } = await LocalNotifications.checkPermissions();
    let state = display;
    if (state === 'prompt') {
      const perm = await LocalNotifications.requestPermissions();
      state = perm.display;
    }
    if (state !== 'granted') {
      console.warn('🔕 Cannot schedule reminder: permission not granted');
      return;
    }

    this.currentRequestId = requestId.toString();
    const id = this.toNotifId(requestId);
    const existingNotifications = await LocalNotifications.getPending();
    // console.log('@@notification:: Existing notifications:', existingNotifications);
    if (existingNotifications.notifications.some(notification => notification.id == id)) {
      await LocalNotifications.cancel({ notifications: [{ id }] });
      await LocalNotifications.removeAllDeliveredNotifications();
    }
    const message = this.getBookingNotificationMessage(bookingIdentifier, new Date(Date.now() + 600000));
    const scheduleAtTime = addMinutes(new Date(), this.platform.is('ios') ? 7 : 5); // 5min on android because scheduled notifications aren't time accurate, need android.permission.USE_EXACT_ALARM permission (which is ONLY for alarm apps)
    await LocalNotifications.schedule({
      notifications: [
        {
          id: id,
          title: this.translate.instant(TranslationKeys.PendingPaymentReminder.Reminder),
          body: message,
          schedule: { at: scheduleAtTime },
          extra: {
            type: 'pending_payment',
            requestId: this.currentRequestId,
            bookingIdentifier: bookingIdentifier,
          } as any,
        },
      ],
    });
  }

  /** Cancel any pending reminder (e.g. on payment or page exit) */
  public async cancelLocalReminder(requestId: number) {
    try {
      const notfId = this.toNotifId(requestId);
      await LocalNotifications.cancel({ notifications: [{ id: notfId }] });
      await LocalNotifications.removeAllDeliveredNotifications();
      // console.log(`❌ OS reminder canceled for request ${this.currentRequestId}`);
      this.currentRequestId = null;
    } catch (error) {
      console.warn('Failed to cancel local reminder', error);
    }
  }

  private toNotifId(requestId: number): number {
    return this.NOTIF_ID_BASE + requestId;
  }

  /** Listen for the native notification firing */
  private listenForNotifications() {
    LocalNotifications.addListener('localNotificationReceived', async (info: LocalNotificationSchema) => {
      //console.log('@@notification:: Received notification:', JSON.stringify(info) ,  this.currentRequestId);

      const extra = info.extra;
      if (extra?.type === 'pending_payment' && extra.requestId == this.currentRequestId) {
        const appState = await App.getState();

        if (appState.isActive) {
          // 1) prevent the native banner
          LocalNotifications.cancel({ notifications: [{ id: this.toNotifId(+extra.requestId) }] }).then();
          LocalNotifications.removeAllDeliveredNotifications().then();
          const message = this.getBookingNotificationMessage(extra.bookingIdentifier, new Date(info.schedule.at));

          // 2) show your Ionic alert instead
          this.ui.showMessage(
            message,
            this.translate.instant(TranslationKeys.PendingPaymentReminder.Reminder),
            null,
            this.translate.instant(TranslationKeys.PendingPaymentReminder.Continue),
            true
          );
        }
      }
      // const pending = await LocalNotifications.getPending();
      // console.log('@@notification:: Pending notifications on listenForNotifications:', pending);
    });
  }

  private async ensureNotificationPermission(): Promise<boolean> {
    let state = await this.getPermissionState();
    if (state === 'prompt') {
      // ask the user once
      const perm = await LocalNotifications.requestPermissions();
      state = perm.display;
    }
    // now, state is 'granted' or 'denied'
    return state !== 'denied';
    // => true if 'granted' or (prompt then granted), false only if explicit deny
  }

  private async getPermissionState(): Promise<string> {
    const { display } = await LocalNotifications.checkPermissions();
    return display; // 'granted' | 'prompt' | 'denied'
  }

  private getBookingNotificationMessage(bookingIdentifier: string, bookingDate: Date): string {
    return `${this.translate.instant(TranslationKeys.PendingPaymentReminder.Booking)} #{${bookingIdentifier}} ${this.translate.instant(
      TranslationKeys.PendingPaymentReminder.Pending
    )} ${this.ui.formatTime(bookingDate, 'HH:mm')} ${this.translate.instant(TranslationKeys.PendingPaymentReminder.Confirmation)}`;
  }
}
